import React, { useEffect, useState, useRef } from "react";
import "./adminPictureUpload.css";
import PictureUploadAdmin from "./PictureUploadAdminComp";
import { CREATE_BA_GROUP } from "../../../../utils/GraphQL/mutations";
import { GET_JOB_OPTIONS_LIST } from "../../../../utils/GraphQL/queries";
import { useMutation, useQuery } from "@apollo/client";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import PictureFormTest from "../../../../utils/formValidators/adminBAGFormCheck";
import BdrDisclosure from "../../../../components/BdrDisclosure/BdrDisclosure";
import BdrCheckbox from "../../../../components/BdrCheckbox/BdrCheckbox";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { useMediaQuery } from "@mui/material";
import EventDateTimeInput from "../EventDateTimeInput/EventDateTimeInput";
import ClientSearchInput from "../ClientSearchInput/ClientSearchInput";

const clearedClient = {
  firstName: "",
  lastName: "",
  _id: "",
};

/*
 * The stain list the form used to carry, inline, as thirteen radio buttons.
 *
 * It is a FALLBACK now, not the list. Stains come from the server
 * (`getJobOptionsList`, listType "stain") — the same query Job Form Review
 * has always used — so adding or renaming a stain in Defaults Setup reaches
 * both pages instead of only one. Keeping the old strings means a server that
 * returns nothing leaves a usable form rather than an empty dropdown, and the
 * field says which list it is showing.
 */
const LEGACY_STAIN_CHOICES = [
  "Ipswich Pine",
  "Golden Oak",
  "Natural",
  "Provincial",
  "Gunstock",
  "Special Walnut",
  "Red Oak",
  "Dark Walnut",
  "Jacobean",
  "Red Mahogany",
  "Ebony",
  "True Black",
];

/* "Other" is a MODE, not a stain: picking it reveals a free-text field and the
   typed value is what gets saved. It is appended to whichever list is in use
   and is never one of the server's options. */
const OTHER_STAIN = "Other";

// The keys must match `refinisherState`'s. This list has always been
// hard-coded on this form — unlike Door Margin, which reads refinishers from
// the server. Left as it was: changing it changes what `BAGroup.refinishers`
// stores, which is a data decision, not a formatting one.
const REFINISHER_BOXES = [
  { name: "kyle", label: "Kyle" },
  { name: "taylor", label: "Taylor" },
  { name: "neil", label: "Neil" },
  { name: "goose", label: "Goose" },
  { name: "seamus", label: "Seamus" },
  { name: "gary", label: "Gary" },
];

const clearBAGForm = {
  beforeId: "",
  afterId: "",
  assignToClientId: null,
  weekGalleryDisplay: true,
  dateEntered: "",
  refinishers: [],
  stainColor: "",
};

const clearRefinishers = {
  kyle: null,
  taylor: null,
  goose: null,
  seamus: null,
  gary: null,
  neil: null,
};

const AdminPictureUpload = ({ handleNewGroupId, handleSnack, inPanel = false }) => {
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [uploadClearStateTrigger, setUploadClearStateTrigger] = useState(false);
  const [formResetTrigger, setFormResetTrigger] = useState(false);
  const [barCollapsed, setBarCollapsed] = useState(true);
  const [newSavedGroupId, setNewSavedGroupId] = useState("");
  const [stainColorChoice, setStainColorChoice] = useState(null);
  const [BAGEpoch, setBAGEpoch] = useState("");
  const smaller900 = useMediaQuery("(max-width:900px)");
  const adminPictureScrollRef = useRef(null);
  const [selectedClient, setSelectedClient] = useState({ ...clearedClient });
  const [BAGroupForm, setBAGroupForm] = useState({ ...clearBAGForm });
  const [refinisherState, setRefinisherState] = useState({
    ...clearRefinishers,
  });
  //for triggering error helper texts on inputs when they fail the checks
  const [errorState, setErrorState] = useState({
    otherStainInput: false,
  });
  // for creating new BAGroup
  const [createBAG, { loading: BAGCreateLoading }] =
    useMutation(CREATE_BA_GROUP);

  /* The stain list, from the server. Same query and same listType as Job Form
     Review, so the two pages cannot drift apart. */
  const { data: stainData } = useQuery(GET_JOB_OPTIONS_LIST, {
    variables: { listType: "stain" },
  });
  const serverStains = stainData?.getJobOptionsList || [];
  const usingLegacyStains = serverStains.length === 0;
  /* TITLES, not ids. `BAGroup.stainColor` is a plain string and always has
     been, so the option's title goes onto the form exactly as the radio's
     label used to — this changes where the list comes from, not what is
     saved. (Job Form Review stores an id because its schema has one.) */
  const stainChoices = [
    ...(usingLegacyStains
      ? LEGACY_STAIN_CHOICES
      : serverStains.map((opt) => opt.title)),
    OTHER_STAIN,
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    addRefinishersToForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refinisherState]);

  useEffect(() => {
    if (formResetTrigger) {
      setFormResetTrigger(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formResetTrigger]);

  useEffect(() => {
    setBAGroupForm((prevState) => ({
      ...prevState,
      dateEntered: BAGEpoch,
    }));
  }, [BAGEpoch]);

  /*
   * The stain choice drives `stainColor`, and "Other" clears it.
   *
   * SPLIT FROM THE DATE EFFECT ON PURPOSE. Both fields were written by one
   * effect keyed on `[stainColorChoice, BAGEpoch]`, so picking "Other",
   * typing a custom stain, and THEN touching the date re-ran the effect and
   * wrote `stainColor: "Other"` back over what was typed — saving the literal
   * word "Other" as the stain. Two independent values, two effects.
   *
   * Selecting "Other" stores "" rather than "Other" so the validator's
   * "must not still be Other or empty" check has something to fail on until
   * the free-text field is filled in.
   */
  useEffect(() => {
    setBAGroupForm((prevState) => ({
      ...prevState,
      stainColor: stainColorChoice === OTHER_STAIN ? "" : stainColorChoice,
    }));
  }, [stainColorChoice]);

  // form validation class for otherInput
  const uploadInputCheck = new PictureFormTest(BAGroupForm);
  /* Read during render — see the validator's note on why the checks are no
     longer async. This is the list the submit row shows. */
  const missingFields = uploadInputCheck.missingFields();

  // clears state
  const stateCleanup = () => {
    setBAGroupForm({ ...clearBAGForm });
    setRefinisherState({ ...clearRefinishers });
    setSelectedClient({ ...clearedClient });
    setStainColorChoice(null);
    setBAGEpoch("");
    setUploadClearStateTrigger(true);
    setFormResetTrigger(true);

    /* Optional: inside the Image Groups panel this element is not the
       scroller, and a missing node here would throw mid-reset and leave the
       form half-cleared. */
    const div = document.getElementById("admin-create-bag-height-marker");
    if (div) div.scrollTop = 0;
  };

  // CB for PictureUploadAdmin to reset clear state trigger
  const pictureUploadClearStateTriggerReset = () => {
    setUploadClearStateTrigger(false);
  };

  // changes before id on state
  const changeBeforeId = (photoId) => {
    setBAGroupForm((prevState) => ({
      ...prevState,
      beforeId: photoId,
    }));
  };

  // changes after id on state
  const changeAfterId = (photoId) => {
    setBAGroupForm((prevState) => ({
      ...prevState,
      afterId: photoId,
    }));
  };

  // added loading state for fetch call
  const changeLoading = async (newState) => {
    setLoadingSaved(newState);
    return;
  };

  const handleCheckbox = (event) => {
    let checked = event.target.checked;
    let name = event.target.name;

    setRefinisherState((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleChange = async (event) => {
    let value = event.target.value;
    let name = event.target.name;

    /* `weekGalleryDisplay` used to arrive as the string "Yes"/"No" from a
       radio pair and be converted here. It is a checkbox now, so the boolean
       arrives as one and `handleGalleryToggle` writes it directly. */

    if (name === "stainColorChoice") {
      setStainColorChoice(value);
    }

    if (name === "otherStainInput") {
      setBAGroupForm((prev) => ({
        ...prev,
        stainColor: value,
      }));

      return;
    }

    setBAGroupForm((prev) => ({
      ...prev,
      [event.target.name]: value,
    }));
  };

  const handleGalleryToggle = (event) => {
    const checked = event.target.checked;

    setBAGroupForm((prev) => ({
      ...prev,
      weekGalleryDisplay: checked,
    }));
  };

  const addRefinishersToForm = () => {
    const refinisherArr = [];
    if (refinisherState.neil === true) {
      refinisherArr.push("Neil");
    }
    if (refinisherState.taylor === true) {
      refinisherArr.push("Taylor");
    }
    if (refinisherState.kyle === true) {
      refinisherArr.push("Kyle");
    }
    if (refinisherState.goose === true) {
      refinisherArr.push("Goose");
    }
    if (refinisherState.seamus === true) {
      refinisherArr.push("Seamus");
    }
    if (refinisherState.gary === true) {
      refinisherArr.push("Gary");
    }

    setBAGroupForm((prev) => ({
      ...prev,
      refinishers: refinisherArr,
    }));
  };

  //meant for clearing error state and message when input is focused on
  const clearError = (event) => {
    setErrorState({
      ...errorState,
      [event.target.name]: false,
    });
  };

  //used for handling selected client to assign uploaded pictures to
  const assignPicsFunc = (selectedClient) => {
    setBAGroupForm((prevState) => ({
      ...prevState,
      assignToClientId: selectedClient?._id,
    }));

    setSelectedClient({
      firstName: selectedClient?.firstName || "",
      lastName: selectedClient?.lastName || "",
      _id: selectedClient?._id || "",
    });
  };

  //used for clearing selected client
  const clearSelectedClient = () => {
    setBAGroupForm((prevState) => ({
      ...prevState,
      assignToClientId: null,
    }));

    setSelectedClient({
      firstName: "",
      lastName: "",
      _id: "",
    });
  };

  // validation check handler for "other" option and dateText inputs
  const checkError = async (event) => {
    let value = event.target.value;

    //value has to be flipped because the methods return true for valid input
    if (event.target.name === "otherStainInput") {
      value = await uploadInputCheck.testOther();
    }

    setErrorState({
      ...errorState,
      [event.target.name]: !value,
    });

    return;
  };

  const timeSelected = (timeStr) => {
    setBAGEpoch(timeStr);
  };

  return (
    <div
      id="admin-create-bag-height-marker"
      className={inPanel ? "" : "bdr-v2-admin-container"}
      ref={adminPictureScrollRef}
    >
      <BdrDisclosure
        bare={inPanel}
        open={!barCollapsed}
        label="Create B&A Group"
        icon={<AddPhotoAlternateIcon />}
        onToggle={() => {
          setTimeout(() => {
            if (smaller900 === true && barCollapsed === true) {
              const navHeight = document.querySelector(
                "#admin-nav-height-marker"
              )?.offsetHeight;
              const pagePos = adminPictureScrollRef.current.offsetTop;

              const totalMobileScroll = pagePos - navHeight - 5;
              window.scrollTo(0, totalMobileScroll);
            }

            if (smaller900 === false && barCollapsed === true) {
              adminPictureScrollRef.current.scrollIntoView({ block: "start" });
            }

            if (barCollapsed === false) {
              window.scrollTo({ top: 0 });
            }
          }, 500);
          setBarCollapsed((prev) => !prev);
        }}
      >
        <div className="bdr-v2-content">
          {newSavedGroupId?.length > 0 && (
            <div className="bag-v2-saved-id">New Group Id: {newSavedGroupId}</div>
          )}

          {/* JOB DETAILS — the two typed facts about the group, on one line
              until they stop fitting. */}
          <div className="bdr-v2-section">
            <h3 className="bdr-v2-section-label">Job details</h3>

            <div className="bag-v2-field-row">
              <div
                className="bag-v2-field"
                role="group"
                aria-label="Date completed"
              >
                {/* A span, not a `<label htmlFor>`. The date control is a
                    composite — a button that opens a calendar — with an id it
                    generates itself and does not accept from a caller, so a
                    `for` here would point at nothing. The group carries the
                    accessible name and this is its visible part.

                    Its own `label` prop is not used because that renders an
                    inline side-label (`.evt-dt-label`, `min-width: 65px`),
                    which would not line up with the stacked field beside it. */}
                <span className="bdr-field-label">Date completed</span>
                <EventDateTimeInput
                  key={formResetTrigger}
                  onChange={timeSelected}
                  defaultTime={{ hour: 10, minute: 0, amPm: "AM" }}
                  hideTime={true}
                />
              </div>

              {/*
                STAIN — a dropdown over the server's list.

                It was thirteen hardcoded radio buttons in a box of their own,
                which is thirteen rows of chrome for one answer and a list that
                could silently disagree with Job Form Review's. `BdrInput` with
                `options` renders the app's own popup list, so this matches the
                stain field on Job Form Review without reaching for a second
                component.
              */}
              <div className="bag-v2-field">
                <BdrInput
                  label="Stain color"
                  name="stainColorChoice"
                  value={stainColorChoice || ""}
                  onChange={handleChange}
                  placeholder="-- Select stain --"
                  options={stainChoices}
                  helper={
                    usingLegacyStains
                      ? "Showing the built-in list — no stains are set up on the server."
                      : undefined
                  }
                />

                {stainColorChoice === OTHER_STAIN && (
                  <BdrInput
                    label="Custom stain"
                    name="otherStainInput"
                    /* Controlled: an uncontrolled field kept its text through
                       a form reset and through switching away from "Other". */
                    value={BAGroupForm.stainColor || ""}
                    onChange={handleChange}
                    onBlur={checkError}
                    onFocus={clearError}
                    placeholder="Name the stain"
                    error={
                      errorState.otherStainInput
                        ? "Please enter a stain name."
                        : undefined
                    }
                  />
                )}
              </div>
            </div>
          </div>

          {/* PHOTOS */}
          <div className="bdr-v2-section">
            <h3 className="bdr-v2-section-label">Before / after photos</h3>

            <div className="bag-v2-upload-row">
              <div className="bag-v2-upload-col">
                <h4>Before</h4>
                <PictureUploadAdmin
                  changePhotoIds={changeBeforeId}
                  changeLoading={changeLoading}
                  BoaProp={"before"}
                  clearState={uploadClearStateTrigger}
                  clearStateTriggerCB={pictureUploadClearStateTriggerReset}
                  changeDateCompleted={BAGEpoch}
                />
                <span className="pic-id-text">
                  {BAGroupForm.beforeId && (
                    <span className="bag-v2-photo-id">
                      Id: {BAGroupForm.beforeId}
                    </span>
                  )}
                </span>
              </div>

              <div className="bag-v2-upload-col">
                <h4>After</h4>
                <PictureUploadAdmin
                  changePhotoIds={changeAfterId}
                  changeLoading={changeLoading}
                  BoaProp={"after"}
                  clearState={uploadClearStateTrigger}
                  clearStateTriggerCB={pictureUploadClearStateTriggerReset}
                  changeDateCompleted={BAGEpoch}
                />
                <span className="pic-id-text">
                  {BAGroupForm.afterId && (
                    <span className="bag-v2-photo-id">
                      Id: {BAGroupForm.afterId}
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* REFINISHERS */}
          <div className="bdr-v2-section">
            <h3 className="bdr-v2-section-label">Refinishers</h3>
            {/* Was six hand-repeated FormControlLabel/Checkbox pairs, each
                carrying its own copy of the same `sx` — including the accent
                written out as a literal six times. The names were already
                hard-coded here; that has not changed, only the repetition. */}
            <div className="bag-v2-checkbox-row">
              {REFINISHER_BOXES.map((refinisher) => (
                <BdrCheckbox
                  key={refinisher.name}
                  size="sm"
                  name={refinisher.name}
                  label={refinisher.label}
                  checked={!!refinisherState?.[refinisher.name]}
                  onChange={handleCheckbox}
                />
              ))}
            </div>
          </div>

          {/* GALLERY — one boolean, so one checkbox.

              It was a "Display in week gallery?" Yes/No radio pair, which is a
              two-option control for a value that only has one bit, in a padded
              box the size of the photo uploaders. It also never reset: the
              radio was not given `resetForm`, so after a save the buttons kept
              the old pick while the form state had gone back to true. Bound
              straight to the form value, the reset is the state reset. */}
          <div className="bdr-v2-section">
            <h3 className="bdr-v2-section-label">Gallery</h3>
            <BdrCheckbox
              name="weekGalleryDisplay"
              label="Show in the week gallery"
              helper="Week gallery groups appear on the public site's recent work."
              checked={!!BAGroupForm.weekGalleryDisplay}
              onChange={handleGalleryToggle}
            />
          </div>

          {/* Client Assignment */}
          <div className="bdr-v2-section">
            <h3 className="bdr-v2-section-label">
              {BAGroupForm?.assignToClientId
                ? "Assigned To"
                : "Assign to Client"}</h3>
            {BAGroupForm?.assignToClientId === null && (
              <ClientSearchInput returnResults={assignPicsFunc} />
            )}
            {BAGroupForm?.assignToClientId && (
              <div className="bag-v2-assigned-box">
                <h4>
                  {`${selectedClient?.firstName} ${selectedClient?.lastName}`?.trim()}
                </h4>
                <button
                  className="bag-v2-clear-btn"
                  onClick={clearSelectedClient}
                >
                  Clear
                </button>
                <span className="bag-v2-assigned-note">
                  *Pictures will not assign till group is uploaded*
                </span>
              </div>
            )}
          </div>

          {/* SUBMIT — with the reason it is not ready, when it is not.

              The button used to call `uploadValid()`, get false and return
              with no message and no marked field, so an unfinished form and a
              broken one looked identical. The list comes from the validator
              itself (`missingFields()`), not a second copy of the rules. */}
          <div className="bag-v2-submit-row">
            {missingFields.length > 0 && (
              <span className="bag-v2-missing">
                Still needed: <b>{missingFields.join(", ")}</b>
              </span>
            )}

            {BAGCreateLoading ? (
              <span className="bag-v2-saving">Saving...</span>
            ) : (
              <BdrButton
                variant="confirm-solid"
                disabled={loadingSaved || missingFields.length > 0}
                onClick={() => {
                  if (uploadInputCheck.uploadValid()) {
                    createBAG({
                      variables: {
                        beforeId: BAGroupForm.beforeId,
                        afterId: BAGroupForm.afterId,
                        weekGalleryDisplay: BAGroupForm.weekGalleryDisplay,
                        dateEntered: BAGroupForm.dateEntered,
                        refinishers: BAGroupForm.refinishers,
                        stainColor: BAGroupForm.stainColor,
                        assignToClientId: BAGroupForm.assignToClientId,
                      },
                      onCompleted: (data) => {
                        handleNewGroupId(data?.createBAGroup._id);
                        setNewSavedGroupId(data?.createBAGroup._id);
                        if (handleSnack) {
                          handleSnack("New Image Group Created", 2500);
                        }
                        stateCleanup();
                      },
                    });
                  }
                }}
              >
                Upload Group
              </BdrButton>
            )}
          </div>
        </div>
      </BdrDisclosure>
    </div>
  );
};

export default AdminPictureUpload;
